import React, { useState } from 'react';
import {
  Box, Typography, Grid, TextField, Paper, Button, Divider,
  FormControlLabel, Checkbox, MenuItem, Container, Chip, IconButton,
  LinearProgress
} from '@mui/material';
import {
  Person, Badge, School, AccountBalance, Home,
  Work, Security, MedicalInformation, AppRegistration,
  CloudUpload, Close, CheckCircle
} from '@mui/icons-material';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

/* ─────────────────────────────────────────────
   DOCUMENT UPLOAD HELPER COMPONENT
───────────────────────────────────────────── */
const DocUpload = ({ fieldName, label, files, onChange, onRemove }) => {
  const file = files[fieldName];
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
        {label}
      </Typography>
      {file ? (
        <Chip
          icon={<CheckCircle fontSize="small" color="success" />}
          label={file.name}
          onDelete={() => onRemove(fieldName)}
          deleteIcon={<Close />}
          variant="outlined"
          color="success"
          size="small"
          sx={{ maxWidth: '100%' }}
        />
      ) : (
        <Button
          component="label"
          variant="outlined"
          size="small"
          startIcon={<CloudUpload />}
          sx={{ textTransform: 'none', borderStyle: 'dashed' }}
        >
          Upload {label}
          <input
            type="file"
            hidden
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={(e) => {
              if (e.target.files[0]) onChange(fieldName, e.target.files[0]);
            }}
          />
        </Button>
      )}
    </Box>
  );
};

/* ─────────────────────────────────────────────
   MAIN FORM
───────────────────────────────────────────── */
const EmployeeOnboardingForm = () => {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  /* ── FORM STATE ── */
  const [form, setForm] = useState({
    /* PERSONAL */
    fullName: '',
    dateOfBirth: '',
    dateOfJoining: '',
    age: '',
    gender: '',
    bloodGroup: '',
    personalEmail: '',
    currentContactNo: '',
    alternateContactNo: '',
    fatherName: '',
    fatherMobile: '',
    motherName: '',
    motherMobile: '',
    married: false,
    spouseName: '',
    spouseMobile: '',

    /* IDENTITY */
    aadhaarNumber: '',
    panNumber: '',
    rationCardNumber: '',
    voterIdNumber: '',
    drivingLicenseNumber: '',
    passportNumber: '',

    /* EDUCATION */
    tenthPercentage: '',
    highestQualification: '',
    universityInstitution: '',
    yearOfPassing: '',
    educationalQualification: '',
    technicalQualification: '',

    /* BANK */
    bankName: '',
    bankAccountNumber: '',
    ifscCode: '',
    branchAddress: '',
    micrCode: '',
    accountType: '',
    pfUan: '',
    esicNumber: '',

    /* ADDRESS */
    currentAddress: '',
    currentCity: '',
    currentState: '',
    currentPincode: '',
    permanentAddress: '',
    permanentCity: '',
    permanentState: '',
    permanentPincode: '',
    permanentContactNo: '',

    /* EXPERIENCE */
    fresher: true,
    experienceYears: '',
    relevantExperience: '',
    previousCompany: '',
    previousPosition: '',
    employmentFrom: '',
    employmentTill: '',
    reasonForLeaving: '',

    /* DRA */
    DRAQUALIFIED: false,
    draCertificateNumber: '',

    /* EMERGENCY */
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactNumber: '',
    emergencyContactAddress: '',
    emergencyContactName2: '',
    emergencyContactRelation2: '',
    emergencyContactNumber2: '',
    emergencyContactAddress2: '',
    emergencyContactName3: '',
    emergencyContactRelation3: '',
    emergencyContactNumber3: '',
    emergencyContactAddress3: '',

    /* MEDICAL / LEGAL */
    medicalIssues: false,
    medicalDetails: '',
    relativesInCompany: false,
    criminalRecord: false,
    criminalDetails: '',
    financialDefault: false,

    declarationAccepted: false,
    declarationDate: new Date().toISOString().split('T')[0],
  });

  /* ── DOCUMENTS STATE ──
     Key = backend fieldName prefix (e.g. "photoUrl")
     Value = File object
  ── */
  const [docs, setDocs] = useState({});

  /* ── HANDLERS ── */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleDocChange = (fieldName, file) => {
    setDocs(prev => ({ ...prev, [fieldName]: file }));
  };

  const handleDocRemove = (fieldName) => {
    setDocs(prev => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  /* ── SUBMIT ── */
  const handleSubmit = async () => {
    if (!form.fullName || !form.aadhaarNumber || !form.panNumber || !form.declarationAccepted) {
      toast.error('Please fill mandatory fields and accept the declaration');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();

      /* JSON data as Blob so Spring @RequestPart("data") deserializes correctly */
      formData.append(
        'data',
        new Blob([JSON.stringify(form)], { type: 'application/json' })
      );

      /*
        Each document File is named: "<fieldName>_<originalFilename>"
        The backend splits on first "_" to extract fieldName.
      */
      Object.entries(docs).forEach(([fieldName, file]) => {
        const renamedFile = new File([file], `${fieldName}_${file.name}`, { type: file.type });
        formData.append('documents', renamedFile);
      });

      await api.post(
        `/onboarding/submit`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Onboarding submitted successfully!');
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── SECTION HEADER ── */
  const SectionHeader = ({ icon, title }) => (
    <Box sx={{ mt: 5, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        {icon}
        <Typography variant="h6" fontWeight="600" color="primary.main">
          {title}
        </Typography>
      </Box>
      <Divider sx={{ borderBottomWidth: 2, mb: 3 }} />
    </Box>
  );

  /* ────────────────────────── RENDER ────────────────────────── */
  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>

        {/* Banner */}
        <Box sx={{ bgcolor: 'primary.main', p: 4, color: '#fff' }}>
          <Typography variant="h4" fontWeight="700">Employee Onboarding</Typography>
          <Typography variant="subtitle1">
            Please provide your details accurately to complete your registration.
            Fields marked with * are mandatory.
          </Typography>
        </Box>

        {submitting && <LinearProgress />}

        <Box sx={{ p: { xs: 2, md: 5 } }}>

          {/* ══════════════ PERSONAL ══════════════ */}
          <SectionHeader icon={<Person color="primary" />} title="Personal Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Full Name *" name="fullName" value={form.fullName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Date of Birth *" InputLabelProps={{ shrink: true }} name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Age *" name="age" value={form.age} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth type="date" label="Date of Joining *" InputLabelProps={{ shrink: true }} name="dateOfJoining" value={form.dateOfJoining} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Gender *" name="gender" value={form.gender} onChange={handleChange}>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Blood Group *" name="bloodGroup" value={form.bloodGroup} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Personal Email *" name="personalEmail" value={form.personalEmail} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Contact Number *" name="currentContactNo" value={form.currentContactNo} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Alternate Contact Number" name="alternateContactNo" value={form.alternateContactNo} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Father's Name" name="fatherName" value={form.fatherName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Father's Mobile" name="fatherMobile" value={form.fatherMobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Mother's Name" name="motherName" value={form.motherName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Mother's Mobile" name="motherMobile" value={form.motherMobile} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="married" checked={form.married} onChange={handleChange} color="primary" />}
                label="I am Married"
              />
            </Grid>
            {form.married && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Spouse Name" name="spouseName" value={form.spouseName} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Spouse Mobile" name="spouseMobile" value={form.spouseMobile} onChange={handleChange} />
                </Grid>
              </>
            )}
            {/* Photo upload */}
            <Grid item xs={12}>
              <DocUpload fieldName="photoUrl" label="Passport / Profile Photo" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
          </Grid>

          {/* ══════════════ IDENTITY ══════════════ */}
          <SectionHeader icon={<Badge color="primary" />} title="Identity Proofs *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Aadhaar Number *" name="aadhaarNumber" value={form.aadhaarNumber} onChange={handleChange} />
              <DocUpload fieldName="aadhaarDocUrl" label="Aadhaar Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="PAN Number *" name="panNumber" value={form.panNumber} onChange={handleChange} />
              <DocUpload fieldName="panDocUrl" label="PAN Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Ration Card Number" name="rationCardNumber" value={form.rationCardNumber} onChange={handleChange} />
              <DocUpload fieldName="rationCardDocUrl" label="Ration Card Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Voter ID Number" name="voterIdNumber" value={form.voterIdNumber} onChange={handleChange} />
              <DocUpload fieldName="voterIdDocUrl" label="Voter ID Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Driving License Number" name="drivingLicenseNumber" value={form.drivingLicenseNumber} onChange={handleChange} />
              <DocUpload fieldName="drivingLicenseDocUrl" label="Driving License Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Passport Number" name="passportNumber" value={form.passportNumber} onChange={handleChange} />
              <DocUpload fieldName="passportDocUrl" label="Passport Document" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="othersDocUrl" label="Other Document (Optional)" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
          </Grid>

          {/* ══════════════ EDUCATION ══════════════ */}
          <SectionHeader icon={<School color="primary" />} title="Education Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="10th Grade (%)" name="tenthPercentage" value={form.tenthPercentage} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Highest Qualification" name="highestQualification" value={form.highestQualification} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="University / Institution" name="universityInstitution" value={form.universityInstitution} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth label="Year of Passing" name="yearOfPassing" value={form.yearOfPassing} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Educational Qualification" name="educationalQualification" value={form.educationalQualification} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Technical Qualification" name="technicalQualification" value={form.technicalQualification} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="tenthGradeDocUrl" label="10th Grade Certificate" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="degreeCertificateUrl" label="Degree / Highest Qualification Certificate" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
          </Grid>

          {/* ══════════════ BANK ══════════════ */}
          <SectionHeader icon={<AccountBalance color="primary" />} title="Bank & Statutory Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Bank Name" name="bankName" value={form.bankName} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Account Number" name="bankAccountNumber" value={form.bankAccountNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="IFSC Code" name="ifscCode" value={form.ifscCode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Branch Address" name="branchAddress" value={form.branchAddress} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="MICR Code" name="micrCode" value={form.micrCode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select fullWidth label="Account Type" name="accountType" value={form.accountType} onChange={handleChange}>
                <MenuItem value="SAVINGS">Savings</MenuItem>
                <MenuItem value="CURRENT">Current</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="PF UAN" name="pfUan" value={form.pfUan} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="ESIC Number" name="esicNumber" value={form.esicNumber} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="bankPassbookUrl" label="Bank Passbook / Statement" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="bankStatementUrl" label="Bank Statement (3 months)" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
          </Grid>

          {/* ══════════════ ADDRESS ══════════════ */}
          <SectionHeader icon={<Home color="primary" />} title="Address Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Current Address" name="currentAddress" value={form.currentAddress} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Current City" name="currentCity" value={form.currentCity} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Current State" name="currentState" value={form.currentState} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Current Pincode" name="currentPincode" value={form.currentPincode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <DocUpload fieldName="currentAddressProofUrl" label="Current Address Proof" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>

            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Permanent Address" name="permanentAddress" value={form.permanentAddress} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Permanent City" name="permanentCity" value={form.permanentCity} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Permanent State" name="permanentState" value={form.permanentState} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Permanent Pincode" name="permanentPincode" value={form.permanentPincode} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Permanent Contact Number" name="permanentContactNo" value={form.permanentContactNo} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DocUpload fieldName="permanentAddressProofUrl" label="Permanent Address Proof" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
            </Grid>
          </Grid>

          {/* ══════════════ EXPERIENCE ══════════════ */}
          <SectionHeader icon={<Work color="primary" />} title="Experience Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="fresher" checked={form.fresher} onChange={handleChange} />}
                label="I am a Fresher"
              />
            </Grid>
            {!form.fresher && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Total Experience (Yrs)" name="experienceYears" value={form.experienceYears} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Relevant Experience (Yrs)" name="relevantExperience" value={form.relevantExperience} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Previous Company" name="previousCompany" value={form.previousCompany} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Previous Position" name="previousPosition" value={form.previousPosition} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="date" label="Start Date" InputLabelProps={{ shrink: true }} name="employmentFrom" value={form.employmentFrom} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth type="date" label="End Date" InputLabelProps={{ shrink: true }} name="employmentTill" value={form.employmentTill} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Reason for Leaving" name="reasonForLeaving" value={form.reasonForLeaving} onChange={handleChange} />
                </Grid>
                {/* Experience documents */}
                <Grid item xs={12} md={4}>
                  <DocUpload fieldName="offerLetterUrl" label="Offer Letter" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DocUpload fieldName="relievingLetterUrl" label="Relieving Letter" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DocUpload fieldName="payslipUrl" label="Last 3 Months Payslip" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
                </Grid>
              </>
            )}
          </Grid>

          {/* ══════════════ DRA ══════════════ */}
          <SectionHeader icon={<Security color="primary" />} title="DRA Qualification *" />
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox name="DRAQUALIFIED" checked={form.DRAQUALIFIED} onChange={handleChange} />}
                label="Check if DRA Qualified"
              />
            </Grid>
            {form.DRAQUALIFIED && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="DRA Certificate Number" name="draCertificateNumber" value={form.draCertificateNumber} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DocUpload fieldName="draCertificateUrl" label="DRA Certificate" files={docs} onChange={handleDocChange} onRemove={handleDocRemove} />
                </Grid>
              </>
            )}
          </Grid>

          {/* ══════════════ EMERGENCY ══════════════ */}
          <SectionHeader icon={<AppRegistration color="primary" />} title="Emergency Contact Details *" />
          {[1, 2, 3].map((num) => {
            const suffix = num === 1 ? '' : num;
            return (
              <Grid container spacing={2} sx={{ mb: 3 }} key={num}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Emergency Contact {num}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Name" name={`emergencyContactName${suffix}`} value={form[`emergencyContactName${suffix}`]} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="Relation" name={`emergencyContactRelation${suffix}`} value={form[`emergencyContactRelation${suffix}`]} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Number" name={`emergencyContactNumber${suffix}`} value={form[`emergencyContactNumber${suffix}`]} onChange={handleChange} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Address" name={`emergencyContactAddress${suffix}`} value={form[`emergencyContactAddress${suffix}`]} onChange={handleChange} />
                </Grid>
              </Grid>
            );
          })}

          {/* ══════════════ MEDICAL / LEGAL ══════════════ */}
          <SectionHeader icon={<MedicalInformation color="primary" />} title="Medical and Legal Details *" />
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0faf4' }}>
                <FormControlLabel
                  control={<Checkbox name="medicalIssues" checked={form.medicalIssues} onChange={handleChange} />}
                  label="Medical Issues?"
                />
                {form.medicalIssues && (
                  <TextField fullWidth sx={{ mt: 1 }} label="Please describe" name="medicalDetails" value={form.medicalDetails} onChange={handleChange} multiline rows={2} />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0faf4' }}>
                <FormControlLabel
                  control={<Checkbox name="criminalRecord" checked={form.criminalRecord} onChange={handleChange} />}
                  label="Criminal Record?"
                />
                {form.criminalRecord && (
                  <TextField fullWidth sx={{ mt: 1 }} label="Please provide details" name="criminalDetails" value={form.criminalDetails} onChange={handleChange} multiline rows={2} />
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0faf4' }}>
                <FormControlLabel
                  control={<Checkbox name="financialDefault" checked={form.financialDefault} onChange={handleChange} />}
                  label="History of Financial Default"
                />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0faf4' }}>
                <FormControlLabel
                  control={<Checkbox name="relativesInCompany" checked={form.relativesInCompany} onChange={handleChange} />}
                  label="Do you have relatives in the company?"
                />
              </Paper>
            </Grid>
          </Grid>

          {/* ══════════════ DECLARATION ══════════════ */}
          <Box sx={{ mt: 6, p: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
            <FormControlLabel
              control={
                <Checkbox name="declarationAccepted" checked={form.declarationAccepted} onChange={handleChange} />
              }
              label={
                <Typography variant="body2" fontWeight="500">
                  I hereby declare that all information provided is true and correct to the best of my knowledge.
                </Typography>
              }
            />
            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
              Date of Declaration: {form.declarationDate}
            </Typography>
          </Box>

          {/* ══════════════ SUBMIT BUTTON ══════════════ */}
          <Box sx={{ mt: 5, textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting}
              sx={{
                px: 10, py: 1.5, borderRadius: 50,
                fontWeight: 'bold', textTransform: 'none', fontSize: '1.1rem'
              }}
            >
              {submitting ? 'Submitting...' : 'Submit Onboarding Form'}
            </Button>
          </Box>

        </Box>
      </Paper>
    </Container>
  );
};

export default EmployeeOnboardingForm;